import { defineStore } from 'pinia'
import {
  fetchCourierProfileApi,
  postCourierLocationApi,
  updateCourierAvailabilityApi,
} from '@/entities/courier/api/courierApi'
import { getCourierGeolocation } from '@/entities/courier/lib/courierGeolocation'
import {
  COURIER_AVAILABILITY,
  isValidCourierAvailability,
} from '@/entities/courier/model/courierAvailability'
import {
  startCourierRealtime,
  stopCourierRealtime,
} from '@/features/courier-orders/realtime/courierRealtime'
import {
  stopCourierLocationTracking,
  syncCourierLocationTracking,
} from '@/features/courier-orders/realtime/courierLocationTracking'
import { isCourierOnActiveDelivery } from '@/entities/order/model/courierOrderFlow'
import { useCourierOrdersStore } from '@/features/courier-orders/model/useCourierOrdersStore'

export { COURIER_AVAILABILITY }

export const useCourierStore = defineStore('courier', {
  state: () => ({
    courierId: null,
    activeOrderIds: [],
    backendCourierStatus: '',
    availabilityStatus: COURIER_AVAILABILITY.OFFLINE,
    isAvailabilityLoading: false,
    isAvailabilityUpdating: false,
    availabilityError: null,
    hasLoadedProfile: false,
  }),
  getters: {
    isOnline: (state) => state.availabilityStatus === COURIER_AVAILABILITY.ONLINE,
    isOnDelivery: (state) => {
      const ordersStore = useCourierOrdersStore()
      if (ordersStore.orderList.some((order) => isCourierOnActiveDelivery(order))) {
        return true
      }
      if (ordersStore.activeOrder && isCourierOnActiveDelivery(ordersStore.activeOrder)) {
        return true
      }
      return state.activeOrderIds.length > 0 && state.backendCourierStatus === 'BUSY'
    },
    /** Backend returns 409 if any active order exists. */
    canChangeAvailability: (state) => state.activeOrderIds.length === 0,
    isOnDuty: (state) =>
      state.availabilityStatus === COURIER_AVAILABILITY.ONLINE || state.activeOrderIds.length > 0,
  },
  actions: {
    clear() {
      stopCourierLocationTracking()
      this.courierId = null
      this.activeOrderIds = []
      this.backendCourierStatus = ''
      this.availabilityStatus = COURIER_AVAILABILITY.OFFLINE
      this.isAvailabilityLoading = false
      this.isAvailabilityUpdating = false
      this.availabilityError = null
      this.hasLoadedProfile = false
    },
    setActiveOrderIds(ids) {
      const next = Array.isArray(ids) ? ids.map((id) => String(id)).filter(Boolean) : []
      this.activeOrderIds = next
      if (next.length) {
        this.availabilityStatus = COURIER_AVAILABILITY.ONLINE
      }
      syncCourierLocationTracking()
    },
    applyProfileData(data) {
      if (data?.courierId) {
        this.courierId = data.courierId
      }
      this.setActiveOrderIds(data?.activeOrderIds ?? [])
      if (data?.backendStatus) {
        this.backendCourierStatus = data.backendStatus
      } else if (!this.activeOrderIds.length) {
        this.backendCourierStatus = ''
      }
      if (isValidCourierAvailability(data?.status)) {
        this.availabilityStatus = data.status
      }
    },
    async hydrateActiveOrdersFromProfile() {
      const ordersStore = useCourierOrdersStore()
      await ordersStore.fetchOrderList({ force: ordersStore.hasLoadedOrderList })
    },
    async syncRealtimeAfterProfile() {
      if (!this.courierId) {
        stopCourierRealtime()
        return
      }
      await startCourierRealtime()
    },
    async fetchProfile({ force = false } = {}) {
      if (this.isAvailabilityLoading) {
        return
      }
      if (this.hasLoadedProfile && !force) {
        return
      }

      this.isAvailabilityLoading = true
      this.availabilityError = null
      try {
        const data = await fetchCourierProfileApi()
        this.applyProfileData(data)
        this.hasLoadedProfile = true

        await this.hydrateActiveOrdersFromProfile()
        await this.syncRealtimeAfterProfile()
      } catch (error) {
        this.availabilityError = error
        throw error
      } finally {
        this.isAvailabilityLoading = false
      }
    },
    async refreshProfile() {
      this.hasLoadedProfile = false
      await this.fetchProfile({ force: true })
    },
    async setAvailability(status) {
      if (!isValidCourierAvailability(status)) {
        return
      }
      if (!this.canChangeAvailability && status !== this.availabilityStatus) {
        return
      }
      if (status === this.availabilityStatus || this.isAvailabilityUpdating) {
        return
      }

      const previous = this.availabilityStatus
      const previousCourierId = this.courierId
      this.availabilityStatus = status
      this.isAvailabilityUpdating = true
      this.availabilityError = null

      try {
        if (status === COURIER_AVAILABILITY.OFFLINE) {
          stopCourierRealtime()
          stopCourierLocationTracking()
        }

        const data = await updateCourierAvailabilityApi(status)
        if (isValidCourierAvailability(data?.status)) {
          this.availabilityStatus = data.status
        }
        if (data?.courierId) {
          this.courierId = data.courierId
        } else if (status === COURIER_AVAILABILITY.OFFLINE) {
          this.courierId = null
        }
        if (Array.isArray(data?.activeOrderIds)) {
          this.setActiveOrderIds(data.activeOrderIds)
        }

        let locationError = null

        if (status === COURIER_AVAILABILITY.ONLINE) {
          if (!this.courierId) {
            throw new Error('Courier id missing in status response')
          }
          await startCourierRealtime()
          try {
            const position = await getCourierGeolocation()
            await postCourierLocationApi(position)
          } catch (error) {
            locationError = error
          }
        }

        return { locationError }
      } catch (error) {
        this.availabilityStatus = previous
        this.courierId = previousCourierId
        this.availabilityError = error

        if (status === COURIER_AVAILABILITY.ONLINE) {
          stopCourierRealtime()
          try {
            await updateCourierAvailabilityApi(COURIER_AVAILABILITY.OFFLINE)
          } catch {}
        }

        throw error
      } finally {
        this.isAvailabilityUpdating = false
      }
    },
  },
})
